import {
  Box,
  ButtonV2,
  Checkbox,
} from '@ghq-abi/design-system';
import { ChevronDownIcon } from '@ghq-abi/design-system-icons';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

interface MultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: { label: string; value: string }[];
  placeholder: string;
  minWidth?: string;
}

export function MultiSelect({
  value,
  onValueChange,
  options,
  placeholder,
  minWidth = '120px',
}: MultiSelectProps) {
  const { t } = useTranslate();

  const [isOpen, setIsOpen] = useState(false);
  const hasValues = value.length > 0;

  const handleOptionToggle = (optionValue: string) => {
    if (optionValue === '') {
      onValueChange([]);
      return;
    }

    if (value.includes(optionValue)) {
      onValueChange(value.filter(v => v !== optionValue));
    } else {
      onValueChange([...value, optionValue]);
    }
  };

  const handleClearAll = () => {
    onValueChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const option = options.find(opt => opt.value === value[0]);
      return t(option?.label ?? '') || value[0];
    }
    return `${value.length} selected`;
  };

  return (
    <Box css={{ position: 'relative', width: '$fit' }}>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        css={{
          width: 'auto',
          minWidth,
          backgroundColor: hasValues ? '#FFF3C4' : '$white',
          border: '1px solid #CACDD5',
          borderRadius: '4px',
          fontSize: '$sm',
          padding: '$2 $3',
          color: '$black !important',
          fontWeight: hasValues ? '$semibold !important' : '$normal !important',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: '#FFF3C4',
          },
        }}
      >
        <Box as="span">{getDisplayText()}</Box>
        <ChevronDownIcon width="16" height="16" />
      </Box>

      {isOpen && (
        <Box
          css={{
            position: 'absolute',
            top: '100%',
            right: 0,
            backgroundColor: '$white',
            border: '1px solid #CACDD5',

            borderRadius: '4px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '$1',
            minWidth: 'max-content',
            width: 'max-content',
          }}
        >
          <Box
            css={{
              padding: '$2 $3',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minWidth: '200px',
            }}
          >
            <Box as="span" css={{ fontSize: '$sm', fontWeight: '$medium' }}>
              {t('myTickets.selectOptions')}
            </Box>
            {hasValues && (
              <ButtonV2
                variant="secondary"
                size="sm"
                onClick={handleClearAll}
                css={{
                  padding: '$1 $2',
                  fontSize: '$xs',
                  color: '#EF4444',
                  '&:hover': {
                    backgroundColor: '#FEF2F2',
                  },
                }}
              >
                Clear all
              </ButtonV2>
            )}
          </Box>

          {options.map(option => (
            <Box
              key={option.value}
              onClick={() => handleOptionToggle(option.value)}
              css={{
                padding: '$2 $3',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '$3',
                minWidth: '200px',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <Checkbox
                checked={
                  option.value === ''
                    ? value.length === 0
                    : value.includes(option.value)
                }
                onChange={() => { }}
                css={{
                  pointerEvents: 'none',
                  width: '16px',
                  height: '16px',
                  flexShrink: 0,
                }}
              />
              <Box
                as="span"
                css={{
                  fontSize: '$sm',
                  flex: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t(option.label)}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {isOpen && (
        <Box
          onClick={() => setIsOpen(false)}
          css={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
      )}
    </Box>
  );
}
